import type { JSX } from 'react'
import React, { useMemo } from 'react'
import { useLocation } from 'react-router'

import KonvaStumpShade from '@/pages/stainingStudio/stumpShade/KonvaStumpShade'
import { useAppSelector } from '@/store'
import { PLACEMENT } from '@/store/appSlice'

import KonvaColorChecker from './KonvaColorChecker'
import KonvaDentin from './KonvaDentin'
import KonvaEnamel from './KonvaEnamel'
import KonvaFLStumpShade from './KonvaFLStumpShade'
import KonvaFLStumpShadeStaining from './KonvaFLStumpShadeStaining'
import KonvaFLWashBake from './KonvaFLWashBake'
import KonvaFMMaterialAdvice from './KonvaFMMaterialAdvice'
import KonvaFrameWork from './KonvaFrameWork'
import KonvaGingiva from './KonvaGingiva'
import KonvaGingivaCeramic from './KonvaGingivaCeramic'
import KonvaGingivaStaining from './KonvaGingivaStaining'
import KonvaInternalEffect from './KonvaInternalEffect'
import KonvaMLEnamel from './KonvaMLEnamel'
import KonvaMLInternalEffect from './KonvaMLInternalEffect'
import KonvaMaterialAdvice from './KonvaMaterialAdvice'
import KonvaOpaquer from './KonvaOpaquer'
import KonvaOpaquerStaining from './KonvaOpaquerStaining'
import KonvaPFMDentin from './KonvaPFMDentin'
import KonvaPFMEnamel from './KonvaPFMEnamel'
import KonvaPFMInternalEffect from './KonvaPFMInternalEffect'
import KonvaWashBake from './KonvaWashBake'

type KonavaElementsProps = {
  hiddenCanvasRef: any
  placement: PLACEMENT
  type: string
}

const KonavaElements: React.FC<KonavaElementsProps> = ({
  hiddenCanvasRef,
  placement,
}) => {
  const location = useLocation()
  const stumpShadeRecipe = useAppSelector(
    (state) => state.stainingStudio.stumpShadeRecipe,
  )

  const [leftElements, rightElements] = useMemo(() => {
    if (!stumpShadeRecipe) return [[], []]

    const makeCards = (placementKey: PLACEMENT) => {
      // stumpShadeRecipe's keys may not be directly indexable by the PLACEMENT enum
      // so cast to `any` for the lookup to satisfy TypeScript while preserving runtime behavior.
      const recipesByPlacement = (stumpShadeRecipe as any)[placementKey] || {}
      return Object.values(recipesByPlacement).map((recipe: any) => ({
        component: (
          <KonvaStumpShade
            input={recipe?.input}
            output={recipe?.output}
            tooltipPlacement={placementKey}
          />
        ),
        id: recipe?.input?.basecolor,
      }))
    }

    return [makeCards(PLACEMENT.LEFT), makeCards(PLACEMENT.RIGHT)]
  }, [stumpShadeRecipe])

  const isDigitalTryIn = location.pathname === '/digital-try-in'
  const isStumpShadeRoute = location.pathname === '/staining-studio/stump-shade'
  const is3DPrintedModelStaining =
    location.pathname === '/model-making/3d-printed-model-staining'

  const shouldShowColorChecker =
    isDigitalTryIn && location.search?.includes('color_checker')

  const simpleRouteComponents: Record<string, JSX.Element> = useMemo(
    () => ({
      '/staining-studio/gingiva-staining': <KonvaGingivaStaining />,
      '/staining-studio/wash-bake': <KonvaWashBake />,
      '/full-layering/framework': <KonvaFrameWork />,
      '/micro-layering/material-advice': <KonvaMaterialAdvice />,
      '/full-monolithic/material-advice': <KonvaFMMaterialAdvice />,
      '/model-making/resin-recipe': <KonvaFLStumpShade />,
      '/full-layering/wash-bake': <KonvaFLWashBake />,
      '/pfm-crown/opaquer-staining': <KonvaOpaquerStaining />,
      '/full-layering/dentin': <KonvaDentin />,
      '/pfm-crown/dentin': <KonvaPFMDentin />,
      '/full-layering/internal-effects': <KonvaInternalEffect />,
      '/micro-layering/internal-effects': <KonvaMLInternalEffect />,
      '/pfm-crown/internal-effects': <KonvaPFMInternalEffect />,
      '/gingiva/staining': <KonvaGingiva />,
      '/full-layering/enamel': <KonvaEnamel />,
      '/micro-layering/enamel': <KonvaMLEnamel />,
      '/pfm-crown/enamel': <KonvaPFMEnamel />,
      '/gingiva/ceramic': <KonvaGingivaCeramic />,
      '/pfm-crown/opaquer': <KonvaOpaquer />,
    }),
    [],
  )

  // 1. digital-try-in special handling (with query param)
  if (shouldShowColorChecker) {
    return (
      <KonvaColorChecker
        hiddenCanvasRef={hiddenCanvasRef}
        placement={placement}
      />
    )
  }

  if (isDigitalTryIn) {
    // /digital-try-in without color_checker query => render nothing
    return null
  }

  // 2. stump shade route (needs left/right elements and placement)
  if (isStumpShadeRoute) {
    const elements =
      placement === PLACEMENT.LEFT
        ? leftElements.map((el) => el.component)
        : rightElements.map((el) => el.component)

    return <>{elements}</>
  }

  // 3. 3D printed model staining (needs placement prop)
  if (is3DPrintedModelStaining) {
    return <KonvaFLStumpShadeStaining placement={placement} />
  }

  // 4. all other simple routes from the map
  const simpleComponent = simpleRouteComponents[location.pathname]
  if (simpleComponent) {
    return simpleComponent
  }

  // 5. default: nothing
  return null
}

export default KonavaElements
